<template>
  <section>
    <header>Общая информация</header>

    <div class="subtitle">
      Наименование
    </div>
    <dl>
      <dt>По документам</dt>
      <dd>{{ position?.name || '--' }}</dd>

      <dt>Товар</dt>
      <dd>{{ position?.product?.name || '--' }}</dd>

      <dt>Товар ЭТП</dt>
      <dd>{{ position?.productCategory?.name }}</dd>

      <template v-if="position?.preorderPosition?.id">
        <dt>Оформлен по предзаказу</dt>
        <dd>{{ position.preorderPosition.id }}</dd>
      </template>
    </dl>

    <hr>

    <div class="subtitle">
      Артикул
    </div>
    <dl>
      <dt>Платформа</dt>
      <dd>{{ position?.productCategory?.id || '--' }}</dd>

      <dt>Покупатель</dt>
      <dd>{{ position?.buyersCode ?? '--' }}</dd>

      <dt>Продавец</dt>
      <dd>{{ position?.sellersCode ?? '--' }}</dd>
    </dl>

    <hr>

    <dl>
      <dt>Производитель</dt>
      <dd>{{ position?.manufacturer?.toString() || PLACEHOLDER_NULL }}</dd>

      <template v-if="position?.productExpirationDate && position.isOfferBased">
        <dt>Срок годности</dt>
        <dd>до {{ $core.utils.date.format(position.productExpirationDate) }}</dd>
      </template>

      <dt>Номер заказа</dt>
      <dd v-if="position.isPreorder">{{ position.formattedNumber }} от {{ position.createdAt!.format() }}</dd>
      <dd v-else>{{ orderString }}</dd>

      <template v-if="!position.isOfferBased">
        <dt>Договор</dt>
        <dd>{{ position.contract?.toString() || PLACEHOLDER_NULL }}</dd>
      </template>

      <dt>Автор</dt>
      <dd>{{ position?.createdBy?.name || PLACEHOLDER_NULL }}</dd>

      <dt>Категория</dt>
      <dd>{{ position?.orderCategory?.toString() || PLACEHOLDER_NULL }}</dd>
    </dl>

    <hr>

    <div class="subtitle">
      Ответственные лица
    </div>
    <dl>
      <dt>Поставщик</dt>
      <dd>{{ position?.responsibleSeller?.name || PLACEHOLDER_NULL }}</dd>

      <dt>Покупатель</dt>
      <dd>{{ position?.responsibleBuyer?.name || PLACEHOLDER_NULL }}</dd>
    </dl>

    <hr>

    <div class="subtitle">
      Количество
    </div>
    <dl>
      <dt>Всего</dt>
      <dd>{{ position?.qty }} {{ position?.unit?.toString() }}</dd>

      <template v-if="position?.shipmentStatus">
        <dt>В пути</dt>
        <dd>{{ position?.shipmentStatus?.inDelivery }} {{ position?.unit?.name }}</dd>

        <dt>Получено</dt>
        <dd>{{ position?.shipmentStatus?.receivedByBuyer }} {{ position?.unit?.name }}</dd>

        <dt
          @mouseenter="$core.tooltip.show($event.currentTarget, complainedByReceiptHintText)"
          @mouseleave="$core.tooltip.hide()"
          class="cursor-pointer"
        >
          Недостача
          <VIcon
            theme="outlined"
            material="help_outline"
          />
        </dt>
        <dd
          @mouseenter="$core.tooltip.show($event.currentTarget, complainedByReceiptHintText)"
          @mouseleave="$core.tooltip.hide()"
          class="cursor-pointer"
        >
          {{ position?.shipmentStatus?.complainedByReceipt }} {{ position?.unit?.toString() }}
        </dd>

        <dt
          @mouseenter="$core.tooltip.show($event.currentTarget, complainedBySubstandardHintText)"
          @mouseleave="$core.tooltip.hide()"
          class="cursor-pointer"
        >
          Брак
          <VIcon
            theme="outlined"
            material="help_outline"
          />
        </dt>
        <dd
          @mouseenter="$core.tooltip.show($event.currentTarget, complainedBySubstandardHintText)"
          @mouseleave="$core.tooltip.hide()"
          class="cursor-pointer"
        >
          {{ position?.shipmentStatus?.complainedSubstandard }} {{ position?.unit?.toString() }}
        </dd>
      </template>

      <template v-else>
        <dt>Заказано</dt>
        <dd>{{ position.qtyOrdered?.format(position.unit) || PLACEHOLDER_NULL }}</dd>

        <dt>Осталось</dt>
        <dd>{{ position.qtyLeft?.format(position.unit) || PLACEHOLDER_NULL }}</dd>

        <dt>Страховой запас</dt>
        <dd>{{ position.preorderMonthlyReserve?.format(position.unit) || PLACEHOLDER_NULL }}</dd>
      </template>
    </dl>

    <hr>

    <dl>
      <dt>Описание</dt>
      <dd>{{ position?.offer?.description || PLACEHOLDER_NULL }}</dd>

      <dt>Комментарий к заказу</dt>
      <dd>{{ position.order?.notes || PLACEHOLDER_NULL }}</dd>
    </dl>
  </section>

  <hr>

  <section>
    <header>Финансы</header>

    <div class="finances-columns">
      <div class="column">
        <h2>Продавец</h2>

        <article>
          <h2>Цена</h2>
          <dl>
            <dt :class="{ main: position.isVatIncludedSeller === true }">
              Полная
            </dt>
            <dd
              class="nowrap"
              :class="{ main: position.isVatIncludedSeller === true }"
            >
              {{ position.priceFullSeller.format(position.currency) }}
            </dd>

            <dt
              @mouseenter="$core.tooltip.show($event.currentTarget, priceHintText)"
              @mouseleave="$core.tooltip.hide()"
              class="nowrap cursor-pointer"
            >
              Начальная
              <VIcon
                theme="outlined"
                material="help_outline"
              />
            </dt>
            <dd
              @mouseenter="$core.tooltip.show($event.currentTarget, priceHintText)"
              @mouseleave="$core.tooltip.hide()"
              class="cursor-pointer"
            >
              {{ position.priceSeller.format(position.currency) }}
            </dd>

            <dt class="nowrap">
              НДС {{ position.vatRateSeller }} {{ position.requisites.vatIncludedSellerString }}
            </dt>
            <dd>
              {{ position.isVatPresented ? position.priceFullSeller.getVat(position.isVatIncludedSeller, position.vatRateSeller)?.format(position.currency) : '--' }}
            </dd>

            <dt>&nbsp;</dt>
            <dd>&nbsp;</dd>
          </dl>

          <hr>

          <h2>Сумма</h2>
          <dl>
            <dt class="main">Полная</dt>
            <dd class="main nowrap">{{ position.sumFullSeller.format(position.currency) }}</dd>

            <dt class="nowrap">НДС {{ position.vatRateSeller }} {{ position.requisites.vatIncludedSellerString }}</dt>
            <dd>{{ position.vatSumSeller?.format(position.currency) || '--' }}</dd>

            <dt>&nbsp;</dt>
            <dd>&nbsp;</dd>
          </dl>

          <hr>

          <dl>
            <template v-if="position?.paymentStatus">
              <dt class="main">Оплачено</dt> <dd class="main nowrap">{{ position.paymentStatus.payed?.format(position.currency) ?? '--' }}</dd>
              <dt>В очереди</dt> <dd>{{ position.paymentStatus.inQueue?.format(position.currency) ?? '--' }}</dd>
              <dt>Осталось</dt> <dd>{{ position.paymentStatus.woPaymentRequests?.format(position.currency) ?? '--' }}</dd>
            </template>

            <dt>Предоплата</dt>
            <dd class="finances-pre-terms">
              <div
                v-if="position.prepaymentTerms.length"
                v-for="(preTerm, index) in position.prepaymentTerms"
                :key="index"
              >
                {{ preTerm.toString() }}
              </div>
              <div v-else>--</div>
            </dd>

            <dt>Постоплата</dt>
            <dd class="finances-post-terms">
              <div
                v-if="position?.postpaymentTerms.length"
                v-for="(postTerm, index) in position.postpaymentTerms"
                :key="index"
              >
                {{ postTerm.toString() }}
              </div>
              <div v-else>--</div>
            </dd>

            <template v-if="position?.paymentStatus">
              <dt>План. оплата</dt> <dd>{{ position.plannedPaymentDate?.format() }}</dd>
            </template>
          </dl>

          <PaymentStatusBar
            v-if="position?.paymentStatus"
            class="payment-bar"
            layout="fill"
            :payment-status="position.paymentStatus"
            :currency="position.currency"
            @open="(anchor) => OrdersService.showPositionPaymentsPopup(position, anchor)"
          />
        </article>
      </div>

      <div class="column" v-if="position.priceFullBuyer">
        <h2>Покупатель</h2>

        <article>
          <h2>Цена</h2>
          <dl>
            <dt class="main">Полная</dt>
            <dd class="main nowrap">{{ position.priceFullBuyer.format(position.currency, position.unit) }}</dd>

            <dt
              @mouseenter="$core.tooltip.show($event.currentTarget, priceHintText)"
              @mouseleave="$core.tooltip.hide()"
              class="nowrap cursor-pointer"
            >
              Начальная
              <VIcon
                theme="outlined"
                material="help_outline"
              />
            </dt>
            <dd
              @mouseenter="$core.tooltip.show($event.currentTarget, priceHintText)"
              @mouseleave="$core.tooltip.hide()"
              class="cursor-pointer"
            >
              {{ position.priceBuyer?.format(position.currency, position.unit) ?? '--' }}
            </dd>

            <dt class="nowrap">НДС {{ position.vatRateBuyer }} в т.ч</dt>
            <dd>
              {{ position.isVatIncludedBuyer !== null && position.vatRateBuyer !== null ? position.priceFullBuyer.getVat(position.isVatIncludedBuyer, position.vatRateBuyer).format(position.currency, position.unit) : '--' }}
            </dd>

            <dt>Доставка в т.ч</dt>
            <dd class="nowrap">
              {{ position.deliveryPricePerUnit?.format(position.currency, position.unit) ?? '--' }}
            </dd>
          </dl>

          <hr>

          <h2>Сумма</h2>
          <dl>
            <dt class="main">Полная</dt> <dd class="main nowrap">{{ position.sumFullBuyer?.format(position.currency) ?? '--' }}</dd>
            <dt class="nowrap">НДС {{ position.vatRateBuyer }} в т.ч</dt> <dd>{{ position.vatSumBuyer?.format(position.currency) ?? '--' }}</dd>
            <dt>Доставка в т.ч</dt>
            <dd class="nowrap">
              {{ position.platformDeliverySum?.format(position.currency) ?? '--' }}
            </dd>
          </dl>

          <hr>

          <dl>
            <template v-if="position?.paymentStatus">
              <dt class="main">Оплачено</dt> <dd class="main nowrap">{{ position.paymentStatus.payed?.format(position.currency) ?? '--' }}</dd>
              <dt>В очереди</dt> <dd>{{ position.paymentStatus.inQueue?.format(position.currency) ?? '--' }}</dd>
              <dt>Осталось</dt> <dd>{{ position.paymentStatus.woPaymentRequests?.format(position.currency) ?? '--' }}</dd>
            </template>

            <dt>Предоплата</dt>
            <dd class="finances-pre-terms">
              <div
                v-if="position?.prepaymentTerms.length"
                v-for="(preTerm, index) in position.prepaymentTerms"
                :key="index"
              >
                {{ preTerm.toString() }}
              </div>
              <div v-else>--</div>
            </dd>

            <dt>Постоплата</dt>
            <dd class="finances-post-terms">
              <div
                v-if="position?.postpaymentTerms.length"
                v-for="(postTerm, index) in position?.postpaymentTerms"
                :key="index"
              >
                {{ postTerm.toString() }}
              </div>
              <div v-else>--</div>
            </dd>

            <template v-if="position?.paymentStatus">
              <dt>План. оплата</dt> <dd>{{ position.plannedPaymentDate?.format() }}</dd>
            </template>
          </dl>

          <PaymentStatusBar
            v-if="position?.paymentStatus"
            class="payment-bar"
            layout="fill"
            :payment-status="position.paymentStatus"
            :currency="position.currency"
            @open="(anchor) => OrdersService.showPositionPaymentsPopup(position, anchor)"
          />
        </article>
      </div>
    </div>
  </section>

  <hr>

  <section>
    <header>Доставка</header>

    <dl>
      <dt>Способ</dt>
      <dd>{{ position?.deliveryType?.name || PLACEHOLDER_NULL }}</dd>

      <dt>Оплата</dt>
      <dd>{{ deliveryPaidBy }}</dd>

      <dt>Срок доставки, дней</dt>
      <dd>{{ position.deliveryDays }} {{ $core.utils.regional.ru.pluralize(position.deliveryDays, 'день', 'дня', 'дней') }}</dd>

      <template v-if="position.isPreorder">
        <dt>Период доставки</dt>
        <dd>
          {{ position.preorderShipmentPeriod?.toString({ prefix: 'с', middle: 'по' }) || PLACEHOLDER_NULL }}
        </dd>
      </template>

      <template v-else>
        <dt>Плановая дата отгрузки</dt>
        <dd>{{ $core.utils.date.format(position.plannedShipmentDate) }}</dd>

        <dt>Желаемая дата доставки</dt>
        <dd>{{ $core.utils.date.format(position.desiredReceiptDate) }}</dd>
      </template>

      <template v-if="position?.shipmentStatus">
        <dt>Дата последней отгрузки</dt>
        <dd>{{ position?.shipmentStatus?.lastReceiptDate?.format() ?? '--' }}</dd>

        <dt>Принято от поставщика</dt>
        <dd>{{ position?.shipmentStatus?.receivedFromSeller }} {{ position?.unit?.name }}</dd>

        <dt>Принято покупателем</dt>
        <dd>{{ position?.shipmentStatus?.receivedByBuyer }} {{ position?.unit?.name }}</dd>
      </template>

      <dt>Температурный режим</dt>
      <dd>
        {{ position.storeTemperatureRange.toString({unit: '°C'}) ?? 'Требований к температурному режиму нет' }}
      </dd>

      <dt>Условия хранения</dt>
      <dd>{{ position?.storeRulesText || PLACEHOLDER_NULL }}</dd>
    </dl>
  </section>

  <hr>

  <section>
    <header>Контрагенты</header>

    <article>
      <div class="title">
        Грузоотправитель
      </div>
      <CompanyDetail
        :company="position?.sellerCompany"
        :store="position?.sellerStore"
      />
    </article>

    <hr>

    <article>
      <div class="title">
        Грузополучатель
      </div>
      <CompanyDetail
        :company="position?.buyerCompany"
        :store="position?.buyerStore"
        :buyer-production-site="position?.buyerProductionSite"
      />
    </article>
  </section>
</template>

<style lang="scss" src="./OrderPositionMainSection.scss" />

<script lang="ts">
import CompanyDetail from '@/domains/Companies/components/CompanyDetail/CompanyDetail.vue';
import PaymentStatusBar from '@/domains/Orders/components/PaymentStatusBar/PaymentStatusBar.vue';
import type OrderPosition from "@/domains/Orders/OrderPosition";
import type PreorderPosition from "@/domains/Preorders/PreorderPosition";
import OrdersService   from "@/domains/Orders/OrdersService";
import type PaymentTerm from "@/domains/PaymentTerms/PaymentTerm";
const PLACEHOLDER_NULL = 'Не задано';

// если НДС сверху, то полная цена серая
// соответственно если НДС вкл, то полная цена базовым цветом
export default {
  name:       'OrderPositionMainSection',
  components: {
    CompanyDetail,
    PaymentStatusBar,
  },

  props: {
    position: {
      type:    Object as PropType<OrderPosition | PreorderPosition>,
      default: null,
    },
  },

  setup() {
    return {
      OrdersService
    }
  },

  data() {
    return {
      PLACEHOLDER_NULL,
      complainedByReceiptHintText:     'Это ситуация, когда товара пришло меньше, чем заказывали',
      complainedBySubstandardHintText: 'Это товар, который не соответствуют установленным стандартам качества и не может быть использован',
      priceHintText:                   'Сугубо справочная информация',
    };
  },

  computed: {
    orderString() {
      if (this.position.order?.id) {
        const formattedOrderNumber = this.$core.utils.number.formatWithDelimiter(this.position.order?.id);
        return `${formattedOrderNumber} от ${this.position.order.createdAt?.format()}`;
      } else {
        return PLACEHOLDER_NULL;
      }
    },

    deliveryPaidBy() {
      const {
        deliveryType,
        isDeliveryPaidByBuyer,
        isOfferBased
      } = this.position;

      if ((!this.$core.utils.isObject(deliveryType)) || typeof isOfferBased !== 'boolean') return PLACEHOLDER_NULL;

      /** Только для заказов через СФТ, всегда:
       * Если: Тип доставки: Платформа то Оплата доставки: Платформой
       * Если Тип доставки: Поставщиком, то Оплата доставки: Поставщиком
       * Для прямых сделок связи с доставкой и оплатой определяет менеджер ОЗЛ в рамках договора с поставщиком или в рамках заказа,
       * где условия договора прописаны вручную.
       * Здесь могут быть кросс связи, такие как Доставка Покупателем и оплата доставки Поставщиком.
       * Но это определяет менеджер (реквизит isDeliveryPaidByBuyer)
       */
      if (isOfferBased) {
        if (deliveryType.id === 'byETP') {
          return 'Платформой';
        } else if (deliveryType.id === 'bySeller') {
          return 'Поставщиком';
        }
      } else {
        return isDeliveryPaidByBuyer ? 'Покупателем' : 'Поставщиком';
      }
    },
  },
};
</script>